import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import useTrackPageView from '../../hooks/useTrackPageView';
import {
  PAGE_TYPE,
  CONTENT_TYPE,
  ARTIST_SECTION,
  ARTIST_ACTION_TYPE,
} from '../../constants/gaEnums';
import { trackArtistContentClick } from '../../lib/gaEvents';
import { useUserStore } from '../../stores/userStore';
import { artistApi } from '../../api/artist';
import PageLayout from '../../components/layout/PageLayout';
import ArtistSimpleCard from '../../components/card/ArtistSimpleCard';
import ProgressBar from '../../components/common/ProgressBar';
import { Box, Typography, Stack, Grid, useMediaQuery } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import ArtistCard from '../../components/card/ArtistCard';
import SidebarLayout from '../../components/layout/SidebarLayout';
import theme from '../../theme';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';

const VIEW_SECTIONS = [
  {
    key: 'fullAlbums',
    label: '앨범',
    viewType: 'full-albums',
    type: 'album',
    artistSection: ARTIST_SECTION.FULL_ALBUMS,
  },
  {
    key: 'singles',
    label: '싱글 및 EP',
    viewType: 'singles',
    type: 'album',
    artistSection: ARTIST_SECTION.SINGLES,
  },
  {
    key: 'liveAlbums',
    label: '라이브 앨범',
    viewType: 'live-albums',
    type: 'album',
    artistSection: ARTIST_SECTION.LIVE_ALBUMS,
  },
  {
    key: 'compilationAlbums',
    label: '컴필레이션 앨범',
    viewType: 'compilation-albums',
    type: 'album',
    artistSection: ARTIST_SECTION.COMPILATION_ALBUMS,
  },
  {
    key: 'appearsOnAlbums',
    label: '피쳐링',
    viewType: 'appears-on-albums',
    type: 'album',
    artistSection: ARTIST_SECTION.APPEARS_ON,
  },
  {
    key: 'similarArtists',
    label: '유사한 아티스트',
    viewType: 'similar-artists',
    type: 'artist',
    artistSection: ARTIST_SECTION.SIMILAR_ARTISTS,
  },
];

const ArtistDetail = () => {
  const params = useParams();
  const artistSlug = params.artistSlug;
  const paramSource = params.artistSource;
  const paramId = params.sourceId;
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const fallbackSourceId = searchParams.get('source_id') || state?.sourceId;
  const fallbackSourceType = fallbackSourceId ? state?.sourceType || 'APPLE_MUSIC' : undefined;

  const navigate = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const [artist, setArtist] = useState(null);
  useTrackPageView(
    {
      pageType: PAGE_TYPE.ARTIST,
      contentType: CONTENT_TYPE.ARTIST,
      contentId: artist?.sourceId,
      contentName: artist?.name,
    },
    { ready: !!artist?.sourceId },
  );
  const [views, setViews] = useState({});
  const [loading, setLoading] = useState(true);

  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    const fetchArtist = async () => {
      setLoading(true);
      try {
        const { data } = artistSlug
          ? await artistApi.getArtistBySlug(artistSlug, {
              sourceId: fallbackSourceId,
              sourceType: fallbackSourceType,
            })
          : await artistApi.getArtistById(paramSource, paramId);
        setArtist(data.artist);
        setViews({
          fullAlbums: data.fullAlbums,
          singles: data.singles,
          liveAlbums: data.liveAlbums,
          compilationAlbums: data.compilationAlbums,
          appearsOnAlbums: data.appearsOnAlbums,
          similarArtists: data.similarArtists,
        });
      } catch (error) {
        console.error('Error fetching artist:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArtist();
  }, [artistSlug, paramSource, paramId, fallbackSourceId, fallbackSourceType]);

  const renderSection = (section) => {
    const viewData = views[section.key];
    if (!viewData || !viewData.items || viewData.items.length === 0) return null;

    return (
      <Box key={section.key}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: { xs: 1, md: 2 },
            borderBottom: `1px solid ${theme.palette.grey.line}`,
            mb: { xs: 2, md: 2.5, lg: 3.75 },
          }}
        >
          <Typography fontWeight={700} fontSize={{ xs: '18px', md: '20px', lg: '24px' }}>
            {section.label}
          </Typography>
          {viewData.total > 4 && (
            <Typography
              color={theme.palette.black[4]}
              fontSize={{ xs: 13, md: 14 }}
              onClick={() => {
                trackArtistContentClick(
                  {
                    artistSection: section.artistSection,
                    actionType: ARTIST_ACTION_TYPE.VIEW_ALL,
                    pageType: PAGE_TYPE.ARTIST,
                  },
                  user,
                );
                if (artistSlug) {
                  const qs = new URLSearchParams();
                  if (searchParams.get('source_id')) qs.set('source_id', artist.sourceId);
                  const query = qs.toString() ? `?${qs.toString()}` : '';
                  navigate(`/artists/${artistSlug}/view/${section.viewType}${query}`, {
                    state: { artistName: artist.name },
                  });
                } else {
                  navigate(
                    `/artists/${artist.artistSource.toLowerCase()}/${artist.sourceId}/${section.viewType}?artistName=${encodeURIComponent(artist.name)}`,
                  );
                }
              }}
              sx={{ cursor: 'pointer' }}
            >
              더보기
            </Typography>
          )}
        </Box>
        <Grid container columnSpacing={{ xs: 1.5, md: 2, lg: 2.5 }}>
          {viewData.items.slice(0, isXs ? 3 : 4).map((item, index) => (
            <Grid item xs={4} md={3} key={`${item.sourceId || item.id}-${index}`}>
              {section.type === 'album' ? (
                <AlbumVerticalCard
                  albumId={item.albumId}
                  sourceId={item.sourceId}
                  albumSource={item.albumSource}
                  slug={item.slug}
                  imageUrl={item.albumImageUrl}
                  albumName={item.title}
                  artistId={item.artist?.sourceId}
                  artistSource={item.artist?.artistSource}
                  artistName={item.artist?.name}
                  artistSlug={item.artist?.slug}
                  albumAvgRating={item.averageRating}
                  commentCount={item.commentCount}
                  bookmarkCount={item.bookmarkCount}
                  releaseDate={item.releaseDate}
                  contentRating={item.contentRating}
                  isStarred={item.isStarred}
                  hideBookmarkCount
                  hideReviewMetaOnMobile
                  hideArtist
                  ratingSolo={!isLg}
                  dateSolo={isLg || isXs}
                  smallText
                  artistSection={section.artistSection}
                  pageType={PAGE_TYPE.ARTIST}
                />
              ) : (
                <ArtistCard
                  id={item.id}
                  artistDbId={item.artistId}
                  name={item.name}
                  artistImageUrl={item.artistImageUrl}
                  artistSource={item.artistSource}
                  slug={item.slug}
                  artistSection={section.artistSection}
                  pageType={PAGE_TYPE.ARTIST}
                />
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <PageLayout>
      {artist && !loading && (
        <OpenInAppBanner type="artist" title={artist.name} image={artist.imageUrl} />
      )}
      <ProgressBar isLoading={loading}>
        {artist && (
          <Stack spacing={{ xs: 4, md: 5, lg: 5.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <ArtistSimpleCard name={artist.name} artistImageUrl={artist.imageUrl} />
            </Box>
            <SidebarLayout pageType={PAGE_TYPE.ARTIST}>
              <Stack spacing={{ xs: 3.75, md: 5, lg: 6.25 }}>
                {VIEW_SECTIONS.map((section) => renderSection(section))}
              </Stack>
            </SidebarLayout>
          </Stack>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default ArtistDetail;
